/**
 * The two server codes that say something specific about Test Mode (an admin impersonating an
 * account), in words. One place for both request paths — the axios client (api/client.ts) and
 * the raw-fetch calls (lib/streamAuth.ts authFetch) — so an upload or a stream says the same
 * thing a Save does, instead of "Request failed" or a raw JSON code.
 */
export const TEST_MODE_MESSAGES = {
  // A route an impersonated session may not reach (the gateway denies by default and opens only
  // routes tagged `allowImpersonation`).
  requires_direct_auth:
    'This change can’t be made in Test Mode. It has to be made by the account owner, signed in as themselves.',
  // The impersonation lapsed. Worded so it does not claim the switch back has happened yet: the
  // re-read that confirms it runs alongside, and can fail.
  impersonation_expired:
    'Your test-mode session expired, so that change was not saved. Start test mode again to continue.',
} as const;

export type TestModeCode = keyof typeof TEST_MODE_MESSAGES;

export function isTestModeCode(code: unknown): code is TestModeCode {
  return typeof code === 'string' && Object.prototype.hasOwnProperty.call(TEST_MODE_MESSAGES, code);
}

/** The header the gateway sets on any response to a request that carried a DEAD impersonation
 *  cookie — it has dropped the cookies and answered as the admin's own session. */
export const IMPERSONATION_ENDED_HEADER = 'x-impersonation-ended';

/** A response the client withholds: the impersonation ended, and identity could not be confirmed
 *  as the admin's before the (admin's) payload would have been handed over under the agent's band. */
export const IMPERSONATION_ENDED_UNSYNCED = 'impersonation_ended_unsynced';
export const IMPERSONATION_ENDED_UNSYNCED_MESSAGE =
  'Your test-mode session ended. Reload the page to continue in your own account.';

export type TestModeError = Error & { serverCode: string; status?: number };

/**
 * THE one mapping from a server answer to a Test Mode error, for both request paths — the axios
 * interceptor (api/client.ts) and every raw fetch (lib/streamAuth.ts authFetch). Returns null when
 * the answer is not one of the Test Mode codes, so an ordinary 403 is left to its caller.
 */
export function testModeErrorFor(status: number | undefined, body: unknown): TestModeError | null {
  if (status !== 403) return null;
  const code = (body as { error?: unknown } | null | undefined)?.error;
  if (!isTestModeCode(code)) return null;
  const err = new Error(TEST_MODE_MESSAGES[code]) as TestModeError;
  err.serverCode = code;
  err.status = 403;
  return err;
}

/** The error for a withheld response (see IMPERSONATION_ENDED_UNSYNCED). */
export function impersonationEndedUnsyncedError(): TestModeError {
  const err = new Error(IMPERSONATION_ENDED_UNSYNCED_MESSAGE) as TestModeError;
  err.serverCode = IMPERSONATION_ENDED_UNSYNCED;
  return err;
}
